import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Models, RealtimeResponseEvent } from "appwrite";

import { useUserContext } from "@/context/AuthContext";
import { formatDistanceToNow } from "date-fns";
import { getMyNotifications, markNotificationAsRead } from "@/lib/appwrite/api";
import { appwriteConfig, client } from "@/lib/appwrite/config";

const ITEMS_PER_PAGE = 8;

const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Models.Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { user } = useUserContext();

  const handleClose = () => setIsOpen(false);

  const fetchNotifications = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const res = await getMyNotifications(user.id);
      // Sort by created_at in descending order
      const sortedNotifications = res.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setNotifications(sortedNotifications);
      const unread = sortedNotifications.filter((n) => !n.is_read).length || 0;
      setUnreadCount(unread);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [user]);

  // Real-time subscription with enhanced event handling
  useEffect(() => {
    if (!user) return;

    const subscription = client.subscribe(
      `databases.${appwriteConfig.databaseId}.collections.${appwriteConfig.notificationsCollectionId}.documents`,
      (response: RealtimeResponseEvent<Models.Document>) => {
        const payload = response.payload;
        
        // Handle create, update, and delete events
        if (payload?.receiver_id === user.id) {
          if (response.events.includes("databases.*.collections.*.documents.*.create")) {
            setNotifications(prev => [payload, ...prev]);
            setUnreadCount(prev => prev + 1);
          } else if (response.events.includes("databases.*.collections.*.documents.*.update")) {
            setNotifications(prev => 
              prev.map(notif => 
                notif.$id === payload.$id ? { ...notif, ...payload } : notif
              )
            );
          } else if (response.events.includes("databases.*.collections.*.documents.*.delete")) {
            setNotifications(prev => prev.filter(notif => notif.$id !== payload.$id));
            setUnreadCount(prev => Math.max(0, prev - 1));
          }
        }
      }
    );

    return () => {
      subscription();
    };
  }, [user]);

  const handleNotificationClick = async (notificationId: string) => {
    try {
      await markNotificationAsRead(notificationId);
      setNotifications(prev =>
        prev.map(notif =>
          notif.$id === notificationId ? { ...notif, is_read: true } : notif
        )
      );
      setUnreadCount(prev => (prev > 0 ? prev - 1 : 0));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const loadMore = () => {
    setVisibleCount(prev => prev + ITEMS_PER_PAGE);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="flex-center z-[150] relative">
        <button
          onClick={() => {
            setIsOpen(prev => !prev);
            if (!isOpen) {
              fetchNotifications(); // Refresh notifications when opening dropdown
            }
          }}
          className="relative focus:outline-none p-2"
        >
          <img
            src="/assets/icons/bell.svg"
            alt="Notifications"
            className="h-6 w-6"
          />
          {unreadCount > 0 && (
            <span className="absolute top-0 right-0 flex items-center justify-center h-4 w-4 rounded-full bg-red text-white text-xs font-semibold">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </div>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-[90]"
            onClick={handleClose}
          />

          <div
            className="fixed top-24 left-1/2 -translate-x-1/2 bg-dark-3 rounded-lg w-80 max-h-[70vh] overflow-y-auto shadow-lg p-4 space-y-4 z-[100]"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-light-1 font-semibold text-lg mb-3 text-center">
              Notifications
            </h3>

            {loading ? (
              <p className="text-light-3 text-center">Loading...</p>
            ) : notifications.length > 0 ? (
              <>
                {notifications.slice(0, visibleCount).map((notification) => (
                  <Link
                    key={notification.$id}
                    to={
                      notification.type === "deal" || notification.type === "message"
                        ? `/chats/${notification.sender_id}`
                        : "/"
                    }
                    className={`block text-light-2 hover:text-primary-500 text-sm space-y-1 p-2 rounded ${
                      !notification.is_read ? "bg-dark-4" : ""
                    }`}
                    onClick={() => handleNotificationClick(notification.$id)}
                  >
                    <div className="text-sm font-semibold text-white">
                        {notification.title || "New Notification"}
                      </div>
                      <div className="text-sm text-gray-300">
                        {notification.body}
                      </div>
                    <div className="text-xs text-light-4">
                      {formatDistanceToNow(new Date(notification.created_at), {
                        addSuffix: true,
                      })}
                    </div>
                  </Link>
                ))}

                {visibleCount < notifications.length && (
                  <div className="text-center mt-4">
                    <button
                      onClick={loadMore}
                      className="text-primary-500 text-sm font-semibold hover:underline"
                    >
                      Load More
                    </button>
                  </div>
                )}
              </>
            ) : (
              <p className="text-light-3 text-center text-sm">No notifications</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationDropdown;